import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { useCart } from '../contexts/CartContext';
import ProductCategories from '../components/ProductCategories';
import ProductGrid from '../components/ProductGrid';
import ProductModal from '../components/ProductModal';
import PageBackground from '../components/PageBackground';
import { useLanguage } from '../contexts/LanguageContext';
import './BhaktiStore.css';

const BhaktiStore = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const { addToCart, cartItems, toggleCart } = useCart();
  const imageUrl = '/images/bg.avif';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const fetchProducts = async () => {
      try {
        const response = await apiService.getAllProducts();
        if (response.success) {
          setProducts(response.data);
        } else {
          throw new Error(response.message || 'Failed to fetch products');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    document.body.classList.add('bhakti-store-theme');
    return () => {
      document.body.classList.remove('bhakti-store-theme');
    };
  }, []);

  const toTranslationKey = (value = '') => value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const getTranslatedCategory = (category) => (
    t(`bhaktiStore.categories.${toTranslationKey(category)}`, {}, category)
  );

  const getTranslatedProductName = (product) => (
    t(`bhaktiStore.products.${toTranslationKey(product?.name)}.name`, {}, product?.name || '')
  );

  const isOutOfStock = (product) => {
    const stock = Number(product?.stock);
    return Number.isFinite(stock) && stock <= 0;
  };

  const getStockLimit = (product) => {
    const stock = Number(product?.stock);
    return Number.isFinite(stock) ? Math.max(0, Math.floor(stock)) : Number.POSITIVE_INFINITY;
  };

  // Create a list of unique categories from the products data
  const categories = [
    { id: 'all', name: t('bhaktiStore.categories.allProducts'), icon: 'fas fa-th' },
    ...[...new Set(products.map(p => p.category))].map(category => ({
      id: category.toLowerCase(),
      name: getTranslatedCategory(category),
      icon: 'fas fa-gem' // Default icon
    }))
  ];

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(product => product.category.toLowerCase() === selectedCategory);

  const handleAddToCart = (product) => {
    if (isOutOfStock(product)) {
      toast.error(t('bhaktiStore.messages.outOfStock'));
      return false;
    }

    const stockLimit = getStockLimit(product);
    const existingCartItem = cartItems.find((item) => item._id === product._id);
    if (existingCartItem && existingCartItem.quantity >= stockLimit) {
      window.alert(t('shoppingCart.quantityExceedsStockMessage'));
      return false;
    }

    addToCart(product); // Add item to cart via context

    // Display a custom toast notification
    toast.custom((toastItem) => (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="toast-container"
      >
        <img src={product.images[0]} alt={getTranslatedProductName(product)} className="toast-image" />
        <div className="toast-text">
          <p>{t('bhaktiStore.messages.addedToCart')}</p>
          <span>{getTranslatedProductName(product)}</span>
        </div>
        <button
          onClick={() => {
            toggleCart();
            toast.dismiss(toastItem.id);
          }}
          className="toast-button"
        >
          {t('bhaktiStore.messages.viewCart')}
        </button>
      </motion.div>
    ), {
      position: 'top-center',
      duration: 4000, // Toast will disappear after 4 seconds
    });

    return true;
  };

  const openProductModal = (product) => setSelectedProduct(product);
  const closeProductModal = () => setSelectedProduct(null);

  const handleBuyNow = (product) => {
    if (isOutOfStock(product)) {
      toast.error(t('bhaktiStore.messages.outOfStock'));
      return;
    }
    const addedToCart = handleAddToCart(product); // Use the same toast notification
    if (!addedToCart) {
      return;
    }
    closeProductModal();
    // Delay opening the cart to give the user time to see the toast
    setTimeout(() => {
      toggleCart();
    }, 300);
  };

  return (
    <div className="bhakti-store-page">
      <PageBackground imageUrl={imageUrl} />
      {/* New compact header */}
      <div className="store-page-header">
        <div className="container-header">
          <h1>{t('bhaktiStore.pageHeader.title')}</h1>
          <p>{t('bhaktiStore.pageHeader.subtitle')}</p>
        </div>
      </div>

      <div className="store-main-content">
        <ProductCategories
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />

        {isLoading ? (
          <div className="loading-spinner">{t('bhaktiStore.loadingProducts')}</div>
        ) : error ? (
          <div className="error-message">{t('bhaktiStore.errorPrefix')}: {error}</div>
        ) : (
          <ProductGrid
            products={filteredProducts}
            onAddToCart={handleAddToCart}
            onProductClick={openProductModal}
          />
        )}
      </div>

      <ProductModal
        isOpen={!!selectedProduct}
        onClose={closeProductModal}
        product={selectedProduct}
        onAddToCart={handleAddToCart}
        onBuyNow={handleBuyNow}
      />
    </div>
  );
};

export default BhaktiStore;
